import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, GitCompare } from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { Chip } from "@/components/ui/chip";
import { Kicker } from "@/components/ui/kicker";
import { Button } from "@/components/ui/button";
import { PersonaRow } from "@/components/experience/persona-row";
import { ScrollFade } from "@/components/ui/scroll-fade";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { walkthroughStatusLabel } from "@/components/experience/vocabulary";
import type { WorkspaceTab } from "./use-workspace-search-params";
import { formatDate, formatDuration } from "./workspace-formatters";
import { WorkspaceSidebarAiMeta } from "./workspace-sidebar-ai-meta";

export interface WorkspaceSidebarOverviewProps {
  walkthrough: any;
  scenarioRuns: any[] | undefined;
  runsChronological: any[];
  walkthroughStepsCount: number;
  problemsCount: number;
  activeTab: WorkspaceTab;
  onSelectTab: (tab: WorkspaceTab) => void;
  projectId: string;
  onOpenReport?: () => void;
}

export function WorkspaceSidebarOverview({
  walkthrough,
  scenarioRuns,
  runsChronological,
  walkthroughStepsCount,
  problemsCount,
  activeTab,
  onSelectTab,
  projectId,
  onOpenReport,
}: WorkspaceSidebarOverviewProps) {
  const navigate = useNavigate();
  const walkthroughId = walkthrough.id;
  const isComplete = walkthrough.status === "completed";
  const isFailed = walkthrough.status === "failed";
  const isRunning =
    walkthrough.status === "running" || walkthrough.status === "pending";

  const persona = walkthrough.persona;
  const htmlReport = walkthrough.artifacts?.find(
    (a: any) => a.type === "html_report",
  );

  let isLocal = false;
  if (walkthrough.targetUrl) {
    try {
      const parsed = new URL(walkthrough.targetUrl);
      isLocal =
        ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(
          parsed.hostname,
        ) || parsed.hostname.endsWith(".local");
    } catch {
      isLocal = false;
    }
  }

  return (
    <Tonal padding="sm" className="p-5 space-y-4">
      <div className="flex items-center justify-between border-b border-border/60 pb-3">
        <Kicker as="h3">Run overview</Kicker>
        <Chip
          tone={
            isComplete
              ? "helps"
              : isFailed
                ? "critical"
                : isRunning
                  ? "important"
                  : "muted"
          }
        >
          {walkthroughStatusLabel(walkthrough.status)}
        </Chip>
      </div>

      <div className="space-y-3 text-xs">
        <dl className="grid grid-cols-2 gap-3">
          <div>
            <dt className="text-muted-foreground">Ran</dt>
            <dd className="mt-0.5 font-medium text-foreground">
              {formatDate(walkthrough.startedAt ?? walkthrough.createdAt)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Duration</dt>
            <dd className="mt-0.5 font-medium text-foreground">
              {formatDuration(walkthrough.startedAt, walkthrough.completedAt) ??
                "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Steps observed</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
              {walkthroughStepsCount}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Problems found</dt>
            <dd
              className={cn(
                "mt-0.5 text-base font-semibold tabular-nums",
                problemsCount > 0
                  ? "text-severity-important"
                  : isComplete
                    ? "text-finding-helps"
                    : "text-muted-foreground",
              )}
            >
              {problemsCount}
            </dd>
          </div>
        </dl>

        {/* Run timeline */}
        {scenarioRuns && scenarioRuns.length > 1 && (
          <div className="border-t border-border/60 pt-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground font-medium">Run timeline</p>
              <span className="text-xs text-muted-foreground font-mono">
                {runsChronological.length} runs
              </span>
            </div>
            <ScrollFade className="flex items-center gap-1.5 pb-1 no-scrollbar">
              {runsChronological.map((r) => {
                const num =
                  r.runNumber ??
                  runsChronological.findIndex((x) => x.id === r.id) + 1;
                const isCurrent = r.id === walkthroughId;
                const pCount = r.problemCounts?.found ?? r.findingCount ?? 0;
                return (
                  <SimpleTooltip
                    key={r.id}
                    content={`Run #${num} · ${walkthroughStatusLabel(r.status)} · ${pCount} problems · ${formatDate(r.startedAt ?? r.createdAt) ?? ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!isCurrent) {
                          navigate(
                            `/projects/${projectId}/walkthroughs/${r.id}`,
                          );
                        }
                      }}
                      className={cn(
                        "flex flex-col items-center justify-center min-w-8 h-8 rounded-md border text-xs font-semibold tabular-nums transition-all cursor-pointer",
                        isCurrent
                          ? "border-foreground bg-foreground text-background font-semibold shadow-xs"
                          : "border-border/70 bg-card hover:bg-muted/60 text-muted-foreground hover:text-foreground",
                      )}
                      aria-label={`Jump to Run #${num}`}
                    >
                      <span>#{num}</span>
                      <span
                        className={cn(
                          "size-1.5 rounded-full mt-0.5",
                          r.status === "completed" &&
                            (pCount > 0 ? "bg-amber-500" : "bg-emerald-500"),
                          r.status === "failed" && "bg-destructive",
                          (r.status === "running" || r.status === "pending") &&
                            (isCurrent
                              ? "bg-background animate-pulse"
                              : "bg-foreground animate-pulse"),
                        )}
                      />
                    </button>
                  </SimpleTooltip>
                );
              })}
            </ScrollFade>
            {activeTab !== "compare" &&
              !(isFailed && walkthroughStepsCount === 0) && (
                <Button
                  variant="outline"
                  size="xs"
                  className="w-full justify-center text-xs mt-1"
                  onClick={() => onSelectTab("compare")}
                >
                  <GitCompare className="mr-1.5 size-3.5" />
                  <span>Compare with previous run</span>
                </Button>
              )}
          </div>
        )}

        {persona && (
          <dl className="border-t border-border/60 pt-3">
            <dt className="text-xs text-muted-foreground">Persona</dt>
            <dd className="mt-1">
              <PersonaRow
                variant="compact"
                persona={{
                  id: persona.id,
                  name: persona.name,
                  role: persona.role,
                }}
                href={`/projects/${projectId}/personas/${persona.id}`}
                size="sm"
              />
            </dd>
          </dl>
        )}

        {/* Target URL */}
        {walkthrough.targetUrl && (
          <dl className="border-t border-border/60 pt-3 text-xs">
            <dt className="text-muted-foreground">Target URL</dt>
            {isLocal ? (
              <dd className="mt-1 flex flex-wrap items-center gap-1.5">
                <SimpleTooltip content={walkthrough.targetUrl}>
                  <span className="truncate font-mono text-muted-foreground cursor-help">
                    {walkthrough.targetUrl}
                  </span>
                </SimpleTooltip>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {walkthrough.runnerMode ??
                    (walkthrough.clientOrigin === "local"
                      ? "Local runner"
                      : "Localhost target")}
                </span>
              </dd>
            ) : (
              <dd className="mt-1 flex items-center gap-1.5 truncate">
                <a
                  href={walkthrough.targetUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="truncate font-mono text-muted-foreground hover:text-foreground hover:underline"
                >
                  {walkthrough.targetUrl}
                </a>
                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
              </dd>
            )}
          </dl>
        )}

        {/* AI Execution details */}
        <WorkspaceSidebarAiMeta
          models={walkthrough.models}
          judgeMeta={walkthrough.judgeMeta}
        />

        {htmlReport?.content && onOpenReport && (
          <dl className="border-t border-border/60 pt-3 text-xs">
            <dt className="text-muted-foreground">Report</dt>
            <dd className="mt-1.5 flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                onClick={onOpenReport}
                className="w-full justify-start text-xs"
              >
                <FileText className="mr-1.5 size-3.5" />
                <span>View HTML report</span>
              </Button>
            </dd>
          </dl>
        )}
      </div>
    </Tonal>
  );
}
